<template>
  <Teleport to="body">
    <div v-if="isVisible" class="modal-overlay" :class="{ dark: isDark }" @click.stop>
      <div class="modal-content" @click.stop>
        <div class="header-container">
          <h2 class="modal-title">Pagar Cuenta - {{ cliente?.nombre_cliente }}</h2>
          <button @click="$emit('close')" class="btn-close">&times;</button>
        </div>

        <!-- Información del Cliente -->
        <div class="cliente-info">
          <div class="info-item">
            <strong>Cliente:</strong> {{ cliente?.nombre_cliente }}
          </div>
          <div class="info-item">
            <strong>RTN:</strong> {{ cliente?.rtn_cliente }}
          </div>
          <div class="info-item">
            <strong>Total Deuda:</strong> 
            <span class="deuda-total">{{ formatCurrency(cliente?.saldo_pendiente_total) }}</span>
          </div>
        </div>

        <!-- Facturas Pendientes -->
        <div class="facturas-section">
          <h3>Facturas Pendientes</h3>
          <div v-if="cargandoFacturas" class="loading">Cargando facturas...</div>
          <div v-else-if="facturasPendientes.length === 0" class="no-facturas">
            No hay facturas pendientes
          </div>
          <div v-else class="facturas-list">
            <div 
              v-for="factura in facturasPendientes" 
              :key="factura.id_cuenta"
              class="factura-item"
              :class="{ selected: facturasSeleccionadas.includes(factura.id_cuenta) }"
              @click="toggleFacturaSeleccion(factura.id_cuenta)"
            >
              <div class="factura-checkbox">
                <input 
                  type="checkbox" 
                  :checked="facturasSeleccionadas.includes(factura.id_cuenta)"
                  @change="toggleFacturaSeleccion(factura.id_cuenta)"
                />
              </div>
              <div class="factura-info">
                <div class="factura-numero">{{ factura.numero_factura || 'Sin N° SAR' }}</div>
                <div class="factura-fecha">{{ formatDate(factura.fecha_venta) }}</div>
              </div>
              <div class="factura-monto">
                {{ formatCurrency(factura.saldo_pendiente) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Resumen de Pago -->
        <div class="resumen-pago">
          <div class="resumen-item">
            <span>Facturas Seleccionadas:</span>
            <span>{{ facturasSeleccionadas.length }}</span>
          </div>
          <div class="resumen-item">
            <span>Total a Pagar:</span>
            <span class="total-pagar">{{ formatCurrency(totalAPagar) }}</span>
          </div>
        </div>

        <!-- Configuración de Pago -->
        <div class="pago-config">
          <div class="pago-buttons">
            <button 
              @click="seleccionarTodas" 
              class="btn btn-secondary"
              :disabled="facturasPendientes.length === 0"
            >
              Seleccionar Todas
            </button>
            <button 
              @click="limpiarSeleccion" 
              class="btn btn-secondary"
              :disabled="facturasSeleccionadas.length === 0"
            >
              Limpiar Selección
            </button>
          </div>

          <!-- Monto a Pagar -->
          <div class="form-group">
            <label>Monto a Pagar:</label>
            <div class="monto-controls">
              <input
                type="number"
                v-model.number="montoPago"
                :max="totalAPagar"
                :min="0.01"
                step="0.01"
                :disabled="isModalLoading"
                class="monto-input"
              />
              <button 
                @click="montoPago = totalAPagar" 
                class="btn btn-small"
                :disabled="totalAPagar === 0"
              >
                Pago Total
              </button>
            </div>
            <div v-if="montoPago > totalAPagar" class="error-message">
              El monto no puede ser mayor al total a pagar
            </div>
          </div>

          <!-- Método de Pago -->
          <div class="form-group">
            <label>Método de Pago:</label>
            <select v-model="metodoPago" :disabled="isModalLoading">
              <option value="Efectivo">Efectivo</option>
              <option value="Transferencia">Transferencia</option>
              <option value="Mixto">Mixto (Efectivo + Transferencia)</option>
            </select>
          </div>

          <!-- Campos específicos según método -->
          <div v-if="metodoPago === 'Transferencia'" class="form-group">
            <label>Número de transferencia:</label>
            <input
              type="text"
              v-model="numeroTransferencia"
              maxlength="10"
              placeholder="Número de transferencia"
              :disabled="isModalLoading"
            />
          </div>

          <div v-if="metodoPago === 'Mixto'" class="mixto-fields">
            <div class="form-group">
              <label>Monto en Efectivo:</label>
              <input
                type="number"
                v-model.number="montoEfectivo"
                :max="montoPago"
                :min="0"
                step="0.01"
                :disabled="isModalLoading"
                @input="calcularMontoTransferencia"
             />
           </div>
           <div class="form-group">
             <label>Monto por Transferencia:</label>
             <input
               type="number"
               v-model.number="montoTransferencia"
               :max="montoPago"
               :min="0"
               step="0.01"
               :disabled="isModalLoading"
               @input="calcularMontoEfectivo"
             />
           </div>
           <div class="form-group">
             <label>Número de transferencia:</label>
             <input
               type="text"
               v-model="numeroTransferenciaMixto"
               maxlength="10"
               placeholder="Número de transferencia"
               :disabled="isModalLoading"
             />
           </div>
         </div>

         <!-- Notas -->
         <div class="form-group">
           <label>Notas:</label>
           <textarea
             v-model="notas"
             placeholder="Observaciones del pago..."
             :disabled="isModalLoading"
           ></textarea>
         </div>
       </div>

       <!-- Botones de Acción -->
       <div class="button-container">
         <button
           @click="$emit('close')"
           class="btn btn-secondary"
           :disabled="isModalLoading"
         >
           Cancelar
         </button>
         <button
           @click="confirmarPago"
           :disabled="!isPagoValido || isModalLoading"
           class="btn btn-confirmar-pago"
         >
           {{ isModalLoading ? 'Procesando...' : 'Confirmar Pago' }}
         </button>
       </div>
     </div>
   </div>
 </Teleport>
</template>

<script>
import CuentasPorCobrarService from "../../services/cuentasPorCobrarSolicitudes";
import { useToast } from "vue-toastification";

export default {
  name: "PagarCuentaModal",
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    cliente: {
      type: Object,
      required: true,
    },
    id_usuario: {
      type: Number,
      required: true,
    },
  },
  emits: ['close', 'pago-realizado'],
  
  // AGREGAR ESTOS LIFECYCLE HOOKS
  created() {
  },
  
 mounted() {
  // Detectar dark mode — el modal usa Teleport, así que aplica .dark directamente
  this.isDark = localStorage.getItem('isDarkMode') === 'true';
  this._onToggleDark = () => {
    this.isDark = localStorage.getItem('isDarkMode') === 'true';
  };
  window.addEventListener('poswave:toggle-dark', this._onToggleDark);

  // Si el modal se monta con isVisible=true, cargar facturas
  if (this.isVisible) {
    this.resetModal();
    this.cargarFacturasPendientes();
  }
},

  beforeUnmount() {
    window.removeEventListener('poswave:toggle-dark', this._onToggleDark);
  },
  
  setup() {
    const toast = useToast();
    return { toast };
  },
 data() {
   return {
     isDark: false,
     isModalLoading: false,
     cargandoFacturas: false,
     facturasPendientes: [],
     facturasSeleccionadas: [],
     montoPago: 0,
     metodoPago: "Efectivo",
     numeroTransferencia: "",
     montoEfectivo: 0,
     montoTransferencia: 0,
     numeroTransferenciaMixto: "",
     notas: "",
   };
 },
 computed: {
   totalAPagar() {
     return this.facturasPendientes
       .filter(f => this.facturasSeleccionadas.includes(f.id_cuenta))
       .reduce((total, factura) => total + factura.saldo_pendiente, 0);
   },
   isPagoValido() {
     const tieneFacturasSeleccionadas = this.facturasSeleccionadas.length > 0;
     const tieneMontoValido = this.montoPago > 0 && this.montoPago <= this.totalAPagar;
     
     if (this.metodoPago === 'Transferencia') {
       return tieneFacturasSeleccionadas && tieneMontoValido && this.numeroTransferencia?.length >= 5;
     }
     
     if (this.metodoPago === 'Mixto') {
       const montosMixtoValidos = this.montoEfectivo > 0 && this.montoTransferencia > 0;
       const sumaMontosCorrecta = (this.montoEfectivo + this.montoTransferencia) === this.montoPago;
       return tieneFacturasSeleccionadas && tieneMontoValido && montosMixtoValidos && 
              sumaMontosCorrecta && this.numeroTransferenciaMixto?.length >= 5;
     }
     
     return tieneFacturasSeleccionadas && tieneMontoValido;
   },
 },

// REEMPLAZAR LA SECCIÓN COMPLETA methods Y watch EN PagarCuentaModal.vue

methods: {
   formatCurrency(value) {
     return `L. ${Number(value).toFixed(2)}`;
   },

   formatDate(dateString) {
     if (!dateString) return '—';
     const date = new Date(dateString);
     if (isNaN(date.getTime())) return '—';
     return date.toLocaleDateString("es-HN");
   },

   async cargarFacturasPendientes() {
     
     if (!this.cliente?.id_cliente) {
       return;
     }
     
     this.cargandoFacturas = true;
     try {
       
       const facturas = await CuentasPorCobrarService.obtenerFacturasPendientesPorCliente(
         this.cliente.id_cliente, 
         this.id_usuario
       );
       
       
       this.facturasPendientes = facturas || [];
       
     } catch (error) {
       console.error('💥 Error al cargar facturas:', error);
       console.error('💥 Error completo:', error.message);
       console.error('💥 Stack:', error.stack);
       this.toast.error("Error al cargar las facturas pendientes: " + error.message);
     } finally {
       this.cargandoFacturas = false;
     }
   },

   toggleFacturaSeleccion(idCuenta) {
     const index = this.facturasSeleccionadas.indexOf(idCuenta);
     if (index > -1) {
       this.facturasSeleccionadas.splice(index, 1);
     } else {
       this.facturasSeleccionadas.push(idCuenta);
     }
     this.ajustarMontoPago();
   },

   seleccionarTodas() {
     this.facturasSeleccionadas = this.facturasPendientes.map(f => f.id_cuenta);
     this.ajustarMontoPago();
   },

   limpiarSeleccion() {
     this.facturasSeleccionadas = [];
     this.montoPago = 0;
   },

   ajustarMontoPago() {
     this.montoPago = this.totalAPagar;
   },

   calcularMontoTransferencia() {
     this.montoTransferencia = this.montoPago - (this.montoEfectivo || 0);
     if (this.montoTransferencia < 0) this.montoTransferencia = 0;
   },

   calcularMontoEfectivo() {
     this.montoEfectivo = this.montoPago - (this.montoTransferencia || 0);
     if (this.montoEfectivo < 0) this.montoEfectivo = 0;
   },

async confirmarPago() {
    if (!this.isPagoValido || this.isModalLoading) return;


    this.isModalLoading = true;
    try {
        const datosPago = {
            facturas_seleccionadas: this.facturasSeleccionadas,
            monto_pago: this.montoPago,
            metodo_pago: this.metodoPago,
            notas: this.notas,
        };

        // Agregar campos específicos según método
        if (this.metodoPago === 'Transferencia') {
            datosPago.numero_transferencia = this.numeroTransferencia;
        } else if (this.metodoPago === 'Mixto') {
            datosPago.monto_efectivo = this.montoEfectivo;
            datosPago.monto_transferencia = this.montoTransferencia;
            datosPago.numero_transferencia = this.numeroTransferenciaMixto;
        }


        const response = await CuentasPorCobrarService.registrarPagoCredito(datosPago, this.id_usuario);
        

        if (response.success) {
            this.toast.success("Pago registrado exitosamente");
            
            
            // ⚠️ USAR EL SERVICIO SIMPLIFICADO
           setTimeout(async () => {
    try {
        await CuentasPorCobrarService.generarComprobantePago(
            this.cliente.id_cliente, 
            this.id_usuario
        );
        this.toast.info("Comprobante de pago generado");
    } catch (comprobanteError) {
        console.error('💥 Error al generar comprobante:', comprobanteError);
        this.toast.warning("Pago registrado correctamente, pero hubo un problema al generar el comprobante");
    }
}, 1500);
            
            // Emitir evento para actualizar la lista principal
            this.$emit('pago-realizado');
            
        } else {
            throw new Error(response.message || "Error al procesar el pago");
        }
    } catch (error) {
        console.error("💥 Error al confirmar pago:", error);
        console.error("💥 Error completo:", error.message);
        console.error("💥 Stack:", error.stack);
        
        // Mostrar error específico al usuario
        let mensajeError = "Error al procesar el pago";
        if (error.message.includes('facturas válidas')) {
            mensajeError = "No se encontraron facturas válidas para pagar";
        } else if (error.message.includes('permisos')) {
            mensajeError = "No tienes permisos para procesar este pago";
        } else if (error.message.includes('monto')) {
            mensajeError = "Error con el monto del pago";
        } else if (error.message) {
            mensajeError = error.message;
        }
        
        this.toast.error(mensajeError);
    } finally {
        this.isModalLoading = false;
    }
},

   resetModal() {
     this.facturasPendientes = [];
     this.facturasSeleccionadas = [];
     this.montoPago = 0;
     this.metodoPago = "Efectivo";
     this.numeroTransferencia = "";
     this.montoEfectivo = 0;
     this.montoTransferencia = 0;
     this.numeroTransferenciaMixto = "";
     this.notas = "";
     this.isModalLoading = false;
   },
},

watch: {
  isVisible(newVal) {
    if (newVal) {
      this.resetModal();
      this.cargarFacturasPendientes();
    }
  },
  metodoPago() {
    // Reset campos específicos cuando cambia el método
    this.numeroTransferencia = "";
    this.montoEfectivo = 0;
    this.montoTransferencia = 0;
    this.numeroTransferenciaMixto = "";
  },
},


};
</script>

<style scoped>
.modal-overlay {
 position: fixed;
 top: 0;
 left: 0;
 right: 0;
 bottom: 0;
 background-color: rgba(0, 0, 0, 0.7);
 display: flex;
 justify-content: center;
 align-items: flex-start;
 padding: 2vh 0;
 z-index: 1000;
 overflow-y: auto;
}

.modal-content {
 background: white;
 padding: 20px;
 border-radius: 8px;
 width: 700px;
 max-width: 90vw;
 max-height: 95vh;
 overflow-y: auto;
 box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
 margin: auto;
}

.header-container {
 display: flex;
 justify-content: space-between;
 align-items: center;
 margin-bottom: 20px;
 padding-bottom: 15px;
 border-bottom: 1px solid #eee;
}

.modal-title {
 font-size: 20px;
 font-weight: bold;
 margin: 0;
}

.btn-close {
 background: none;
 border: none;
 font-size: 24px;
 cursor: pointer;
 color: #666;
 padding: 0;
 width: 30px;
 height: 30px;
 display: flex;
 align-items: center;
 justify-content: center;
 border-radius: 50%;
}

.btn-close:hover {
 background-color: #f0f0f0;
 color: #000;
}

.cliente-info {
 background-color: #f8f9fa;
 padding: 15px;
 border-radius: 5px;
 margin-bottom: 20px;
 display: grid;
 grid-template-columns: 1fr 1fr 1fr;
 gap: 15px;
}

.info-item {
 font-size: 14px;
}

.deuda-total {
 color: #dc3545;
 font-weight: bold;
 font-size: 16px;
}

.facturas-section {
 margin-bottom: 20px;
}

.facturas-section h3 {
 margin: 0 0 15px 0;
 color: #333;
 font-size: 16px;
}

.loading {
 text-align: center;
 padding: 20px;
 color: #666;
}

.no-facturas {
 text-align: center;
 padding: 20px;
 color: #999;
 font-style: italic;
}

.facturas-list {
 max-height: 300px;
 overflow-y: auto;
 border: 1px solid #ddd;
 border-radius: 5px;
}

.factura-item {
 display: flex;
 align-items: center;
 padding: 12px;
 border-bottom: 1px solid #eee;
 cursor: pointer;
 transition: background-color 0.2s;
}

.factura-item:hover {
 background-color: #f8f9fa;
}

.factura-item.selected {
 background-color: #e3f2fd;
 border-left: 4px solid #2196f3;
}

.factura-item:last-child {
 border-bottom: none;
}

.factura-checkbox {
 margin-right: 15px;
}

.factura-info {
 flex: 1;
}

.factura-numero {
 font-weight: bold;
 color: #333;
}

.factura-fecha {
 font-size: 12px;
 color: #666;
}

.factura-monto {
 font-weight: bold;
 color: #007bff;
}

.resumen-pago {
 background-color: #e8f5e9;
 padding: 15px;
 border-radius: 5px;
 margin-bottom: 20px;
}

.resumen-item {
 display: flex;
 justify-content: space-between;
 margin-bottom: 5px;
}

.resumen-item:last-child {
 margin-bottom: 0;
}

.total-pagar {
 font-weight: bold;
 font-size: 18px;
 color: #2e7d32;
}

.pago-config {
 margin-bottom: 20px;
}

.pago-buttons {
 display: flex;
 gap: 10px;
 margin-bottom: 20px;
}

.form-group {
 margin-bottom: 15px;
}

.form-group label {
 display: block;
 margin-bottom: 5px;
 font-weight: 500;
 color: #333;
}

.form-group input,
.form-group select,
.form-group textarea {
 width: 100%;
 padding: 8px;
 border: 1px solid #ddd;
 border-radius: 4px;
 font-size: 14px;
}

.form-group textarea {
 resize: vertical;
 min-height: 60px;
}

.monto-controls {
 display: flex;
 gap: 10px;
 align-items: center;
}

.monto-input {
 flex: 1;
}

.mixto-fields {
 background-color: #f8f9fa;
 padding: 15px;
 border-radius: 5px;
 margin-bottom: 15px;
}

.btn {
 padding: 8px 16px;
 border: none;
 border-radius: 4px;
 cursor: pointer;
 font-size: 14px;
 transition: all 0.3s ease;
}

.btn-confirmar-pago {
 background-color: #198754;
 color: white;
}

.btn-confirmar-pago:hover:not(:disabled) {
 background-color: #146c43;
}

.btn-secondary {
 background-color: #6c757d;
 color: white;
}

.btn-secondary:hover:not(:disabled) {
 background-color: #545b62;
}

.btn-small {
 padding: 4px 8px;
 font-size: 12px;
}

.btn:disabled {
 opacity: 0.6;
 cursor: not-allowed;
}

.button-container {
 display: flex;
 justify-content: flex-end;
 gap: 10px;
 padding-top: 15px;
 border-top: 1px solid #eee;
}

.error-message {
 color: #dc3545;
 font-size: 12px;
 margin-top: 5px;
}

/* ── Dark mode ─────────────────────────────────────────────── */
/* .dark va en el overlay (Teleport saca el modal del .app-wrapper) */
.dark .modal-content {
  background: #2d2d2d;
  color: #ffffff;
}

.dark .header-container {
  border-color: #404040;
}

.dark .modal-title {
  color: #ffffff;
}

.dark .btn-close {
  color: #ffffff;
}

.dark .btn-close:hover {
  background-color: #404040;
  color: #ffffff;
}

.dark .cliente-info {
  background-color: #1e1e1e;
}

.dark .info-item {
  color: #ffffff;
}

.dark .facturas-section h3 {
  color: #ffffff;
}

.dark .loading,
.dark .no-facturas {
  color: #aaaaaa;
}

.dark .facturas-list {
  border-color: #404040;
}

.dark .factura-item {
  border-color: #404040;
}

.dark .factura-item:hover {
  background-color: #3a3a3a;
}

.dark .factura-item.selected {
  background-color: #1a3a52;
  border-left-color: #4a9eff;
}

.dark .factura-numero {
  color: #ffffff;
}

.dark .factura-fecha {
  color: #aaaaaa;
}

.dark .factura-monto {
  color: #4a9eff;
}

.dark .resumen-pago {
  background-color: #1e1e1e;
}

.dark .resumen-item {
  color: #ffffff;
}

.dark .total-pagar {
  color: #4caf50;
}

.dark .form-group label {
  color: #ffffff;
}

.dark .form-group input,
.dark .form-group select,
.dark .form-group textarea {
  background-color: #2d2d2d;
  border-color: #404040;
  color: #ffffff;
}

.dark .mixto-fields {
  background-color: #1e1e1e;
}

.dark .button-container {
  border-color: #404040;
}

.dark .btn-small {
  background-color: #404040;
  color: #ffffff;
}

.dark .btn-small:hover:not(:disabled) {
  background-color: #505050;
}

/* Media Queries */
@media screen and (max-width: 768px) {
 .modal-content {
   width: 95vw;
   padding: 15px;
 }

 .cliente-info {
   grid-template-columns: 1fr;
   gap: 10px;
 }

 .pago-buttons {
   flex-direction: column;
 }

 .monto-controls {
   flex-direction: column;
   align-items: stretch;
 }

 .button-container {
   flex-direction: column;
 }
}
</style>